import { useState, useEffect, useMemo } from "react";
import { chatAction } from "../../../store/chat";
import { useDispatch, useSelector } from "react-redux";
import { CodeBlock } from "../../Chat/CodeBlock";
import { MemoizedReactMarkdown } from "../../Chat/Markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import remarkHtml from "remark-html";
import emitter from "../../../utils/events";

const ReplyByAI = ({ isNewChat, modelName = "AI", ...props }) => {
  const { gemini, scrollHeight, scrollHeightInstant, isUserScrolling } = props;
  const [currentIndex, setCurrentIndex] = useState(0);
  const isStoped = useSelector((state) => state.chat.isStoped);
  const messageLength = useSelector((state) => state.chat.messageLength);

  const [showLoadingText, setLoadingText] = useState(0);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isNewChat) return;
    let intervalId;
    let isTabVisible = !document.hidden;
    
    if (isStoped) {
      dispatch(chatAction.setIsReplying(false));
      return clearInterval(intervalId);
    }
    if (messageLength && currentIndex >= messageLength) {
      dispatch(chatAction.setIsReplying(false));
      return clearInterval(intervalId);
    }

    if (messageLength && currentIndex >= messageLength) {
      dispatch(chatAction.setIsReplying(false));
      return clearInterval(intervalId);
    }

    // Handle tab visibility changes to ensure response continues even when tab is not active
    const handleVisibilityChange = () => {
      isTabVisible = !document.hidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // AI-style chunked display: show 10-15 words at a time instead of character by character
    const getNextChunk = (text, currentPos) => {
      if (currentPos >= text.length) return text.length;
      
      // Find the next space after 10-15 words
      let wordCount = 0;
      let pos = currentPos;
      
      // Adaptive chunk size based on text length for better UX
      const chunkSize = text.length > 1000 ? 15 : 10; // Larger chunks for longer texts
      
      while (pos < text.length && wordCount < chunkSize) {
        if (text[pos] === ' ' || text[pos] === '\n' || text[pos] === '.' || text[pos] === '!') {
          wordCount++;
        }
        pos++;
      }
      
      // If we're near the end, show the rest
      if (pos >= text.length - 30) {
        return text.length;
      }
      
      return pos;
    };

    // Use a more robust approach that works regardless of tab visibility
    const animateText = () => {
      if (currentIndex < gemini?.length) {
        const nextIndex = getNextChunk(gemini, currentIndex);
        setCurrentIndex(nextIndex);
        
        // Scroll continuously during text generation for smooth experience
        // Only scroll if user is not manually scrolling
        if (scrollHeight && nextIndex % 60 === 0 && !isUserScrolling) {
          setTimeout(() => {
            scrollHeight(); // Use smooth scroll for better experience
          }, 100); // Slower scroll response for smoothness
        }
        
        // Continue animation regardless of tab visibility
        if (nextIndex < gemini?.length) {
          intervalId = setTimeout(animateText, 40);
        } else {
          // Final scroll when ALL text is displayed
          if (scrollHeight && !isUserScrolling) {
            setTimeout(() => {
              scrollHeight(); // Use smooth scroll for final position
            }, 200); // Longer delay for smoother final scroll
          }
        }
      }
    };

    // Start the animation
    animateText();

    return () => {
      clearTimeout(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gemini, currentIndex]);

  useEffect(() => {
    if (isNewChat) {
      if (!gemini) {
        setLoadingText(1);
        setTimeout(() => {
          setLoadingText(2);
          // setTimeout(() => {
          //   setLoadingText(0);
          // }, 10000);
        }, 7000);
      } else {
        setLoadingText(0);
      }
    }
  }, [gemini, isNewChat, isStoped]);

  const preMessage = useMemo(() => {
    if (isNewChat && !gemini) {
      if (isStoped) {
        emitter.emit("abort-chat");
        return <></>;
      }
      return (
        <>
          {showLoadingText === 1 && (
            <div className="animate-fadeIn delay-500">
              <div className="ml-5 w-full animate-pulse delay-500 flex items-center ">
                <p className="animate-fadeOut !font-semibold text-gray-500">
                  Gospel Truth AI thinking...
                </p>
              </div>
            </div>
          )}
          {showLoadingText === 2 && (
            <div className="animate-fadeIn delay-500">
              <div className="ml-5 w-full animate-pulse delay-500 flex items-center ">
                <p className="!font-semibold text-gray-500">Please wait ...</p>
              </div>
            </div>
          )}
        </>
      );
    }
    return <></>;
  }, [isStoped, isNewChat, gemini, showLoadingText, modelName]);

  return (
    <div className="w-full">
      {preMessage}
      <MemoizedReactMarkdown
        className="w-full text-start prose break-words dark:prose-invert prose-p:leading-relaxed prose-pre:p-0 px-3"
        remarkPlugins={[remarkGfm, remarkMath, remarkHtml]}
        components={{
          li({ children }) {
            return <li className="my-3 last:mb-0">{children}</li>;
          },

          a(props) {
            return (
              <a className="text-blue-500" href={props.href}>
                {props.children}
              </a>
            );
          },
          p({ children }) {
            return (
              <p className="whitespace-pre-wrap my-3 last:mb-0">{children}</p>
            );
          },
          code({ node, inline, className, children, ...props }) {
            if (children && children.length) {
              // eslint-disable-next-line eqeqeq
              if (children[0] == "▍") {
                return (
                  <span className="mt-1 cursor-default animate-pulse">▍</span>
                );
              }
              if (Array.isArray(children)) {
                children[0] = children[0].replace("`▍`", "▍");
              }
            }

            const match = /language-(\w+)/.exec(className || "shell");

            if (!node.properties.className || inline) {
              return (
                <code className={`${className ? className : ""}`} {...props}>
                  {children}
                </code>
              );
            }

            return (
              <CodeBlock
                key={Math.random()}
                language={(match && match[1]) || ""}
                value={String(children).replace(/\n$/, "")}
                {...props}
              />
            );
          },
        }}
      >
        {isNewChat ? gemini?.slice(0, currentIndex) : gemini}
      </MemoizedReactMarkdown>
    </div>
  );
};

export default ReplyByAI;
